import * as turf from "@turf/turf";
import type {
  Feature,
  FeatureCollection,
  LineString,
  MultiPolygon,
  Point,
  Polygon,
} from "geojson"; // Import types from geojson library
import type mapboxgl from "mapbox-gl";
import { toast } from "react-toastify";
import i18n from "../../i18n";
import { formatTranslation } from "../../shared/utils/translationUtils";
import { getSubPolygons } from "../../shared/utils/geoUtils";
import {
  buildParallelRhumbLine,
  computeLineExtentKm,
  getGridLinesInfo,
} from "./abLine";

// Drag-handle + drop-feedback colours.
const CONTROL_CIRCLE_COLOR = "rgba(40, 40, 40, 0.8)"; // normal (dark)
const CONTROL_CIRCLE_INVALID_COLOR = "rgba(200, 60, 60, 0.8)"; // occupied target
const DROP_HIGHLIGHT_VALID_COLOR = "rgba(49, 180, 242, 0.9)"; // target grid line
const DROP_HIGHLIGHT_INVALID_COLOR = "rgba(200, 60, 60, 0.9)";
// Two strip lines are "colliding" when their offsets match within this many
// metres (they snap to the same grid/grey line).
const OCCUPIED_TOLERANCE_M = 1;

/**
 * Feature flag for the arrow click/hover step controls on strip lines.
 * Disabled in production; exposed as a mutable config object (rather than a
 * local const) purely so tests can flip it on to exercise that branch
 * without changing the shipped default.
 */
export const stepControlsConfig: { enabled: boolean } = { enabled: false };

/** Last element of a non-empty array (uses Array.at, never indexes by length). */
export const lastItem = <T>(items: T[]): T => {
  const value = items.at(-1);
  if (value === undefined) {
    throw new Error("lastItem called on an empty array");
  }
  return value;
};

function extendLine(
  start: number[],
  end: number[],
  scaleFactor: number
): Feature<LineString> {
  const [dx, dy] = [end[0] - start[0], end[1] - start[1]];
  return turf.lineString([
    [start[0] - dx * scaleFactor, start[1] - dy * scaleFactor],
    [end[0] + dx * scaleFactor, end[1] + dy * scaleFactor],
  ]);
}

// Strip Lines functionality
export interface ABLine {
  name: string;
  id: string | null;
  geojson: {
    type: "Feature";
    geometry: {
      type: "Point"; // This seems incorrect in your data, should be LineString
      coordinates: number[][]; // Array of [lng, lat] pairs
    };
  };
  grid_id: string | null;
  grid_width: number;
}

export interface StripLineResult {
  lines: Array<Feature<LineString>>;
  strips: Array<{
    index: number;
    area: number; // Strip area in square meters (sum over all disjoint pieces)
    stripLength: number; // Length of the strip in meters
    stripWidth: number; // Average width of the strip in meters
    // A strip is the field area within one band. It may be disjoint (across the
    // gaps of a MultiPolygon field, a concave notch, or a hole), hence Polygon or
    // MultiPolygon — every piece is kept (no "largest only" discard).
    geometry: Feature<Polygon | MultiPolygon>;
    boundingBox: {
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    };
    leftBoundary: Feature<LineString> | null; // Left strip line boundary
    rightBoundary: Feature<LineString> | null; // Right strip line boundary
  }>;
}

interface DividerDropTargetArgs {
  insideField: boolean;
  droppedLngLat: {
    lng: number;
    lat: number;
  };
  originalCoordinates: [number, number];
  stripLineIndex: number;
  currentLineDistance: number;
  nearestLineIndex: number | null;
  outermostLineIndex: number | null;
  lineDistanceMetersByIndex: Record<number, number>;
  lineMidpointByIndex: Record<number, [number, number]>;
  fallbackLineIndex: number | null;
}

export const resolveDividerDropTarget = ({
  insideField,
  droppedLngLat,
  originalCoordinates,
  currentLineDistance,
  nearestLineIndex,
  outermostLineIndex,
  lineDistanceMetersByIndex,
  lineMidpointByIndex,
  fallbackLineIndex,
}: DividerDropTargetArgs): {
  snapTargetLineIndex: number;
  targetDistance: number;
  targetCoords: [number, number];
} => {
  const getValidLineIndex = (
    preferredIndex: number | null,
    secondaryIndex: number | null
  ): number => {
    if (
      Number.isInteger(preferredIndex) &&
      preferredIndex !== null &&
      preferredIndex >= 0
    ) {
      return preferredIndex;
    }

    if (
      Number.isInteger(secondaryIndex) &&
      secondaryIndex !== null &&
      secondaryIndex >= 0
    ) {
      return secondaryIndex;
    }

    return 0;
  };

  const currentLineIndex = getValidLineIndex(fallbackLineIndex, null);

  if (!insideField) {
    return {
      snapTargetLineIndex: currentLineIndex,
      targetDistance: Number.isFinite(currentLineDistance)
        ? currentLineDistance
        : 0,
      targetCoords: originalCoordinates,
    };
  }

  const snappedLineIndex = getValidLineIndex(
    nearestLineIndex,
    fallbackLineIndex
  );

  const snappedDistance = Number(lineDistanceMetersByIndex[snappedLineIndex]);
  const targetDistance = Number.isFinite(snappedDistance)
    ? snappedDistance
    : currentLineDistance;

  const snappedCoords = lineMidpointByIndex[snappedLineIndex];
  const targetCoords: [number, number] =
    snappedCoords ??
    ([droppedLngLat.lng, droppedLngLat.lat] as [number, number]);

  return {
    snapTargetLineIndex: snappedLineIndex,
    targetDistance: Number.isFinite(targetDistance) ? targetDistance : 0,
    targetCoords,
  };
};

const roundOffset = (offset: number): number =>
  Math.round(offset * 1000) / 1000;

export const buildSnapGuideOffsets = (lineOffsets: number[]): number[] => {
  const uniqueSortedOffsets = [...new Set(lineOffsets.map(roundOffset))].sort(
    (a, b) => a - b
  );

  const snapOffsets = new Set<number>(uniqueSortedOffsets);

  for (let index = 0; index < uniqueSortedOffsets.length - 1; index += 1) {
    snapOffsets.add(
      roundOffset(
        (uniqueSortedOffsets[index] + uniqueSortedOffsets[index + 1]) / 2
      )
    );
  }

  return [...snapOffsets].sort((a, b) => a - b);
};

const getSnapGuideLines = (
  abLine: ABLine,
  polygonGeometry: Polygon | MultiPolygon
): Array<Feature<LineString>> => {
  const coordinates = abLine.geojson?.geometry?.coordinates;
  const gridWidth = Number(abLine.grid_width);

  if (
    !Array.isArray(coordinates) ||
    coordinates.length < 2 ||
    !Number.isFinite(gridWidth) ||
    gridWidth <= 0
  ) {
    return [];
  }

  try {
    const gridInfo = getGridLinesInfo(
      coordinates[0],
      coordinates[1],
      turf.feature(polygonGeometry) as any,
      gridWidth
    );
    const snapOffsets = buildSnapGuideOffsets(gridInfo.lineOffsets);

    return generateParallelLines(abLine, snapOffsets, polygonGeometry);
  } catch (error) {
    console.warn("Failed to build strip snap guide lines", error);
    return [];
  }
};

/**
 * Generate parallel lines at specified distances from a baseline
 * @param abLine - The baseline with two coordinate points
 * @param offsetDistances - Array of distances in meters (positive = right, negative = left)
 * @param polygonGeometry - The field polygon to determine line extent
 * @returns Array of parallel line features intersected with polygon
 */
export const generateParallelLines = (
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: Polygon | MultiPolygon
): Array<Feature<LineString>> => {
  // Extract coordinates from the AB line
  const coordinates = abLine.geojson.geometry.coordinates;

  if (coordinates.length < 2) {
    throw new Error("AB line must have at least 2 coordinate points");
  }

  const field = turf.feature(polygonGeometry) as Feature<
    Polygon | MultiPolygon
  >;

  // Use the SAME geometry the machinery grid is drawn from so strip lines overlap
  // the grid/grey lines exactly:
  //  - bearing from the original (short) AB segment (NOT a 1000×-extended line,
  //    whose great-circle bearing differs for a diagonal AB line), and
  //  - buildParallelRhumbLine for the perpendicular rhumb offset.
  // Previously this used transformTranslate of an extended baseline with the
  // extended-line bearing, which shifted strips laterally off the machinery grid.
  const start = coordinates[0];
  const end = coordinates[1];
  const abBearing = turf.bearing(turf.point(start), turf.point(end));
  const extentKm = computeLineExtentKm(start, field);

  const parallelLines: Array<Feature<LineString>> = [];

  offsetDistances.forEach((distance, index) => {
    try {
      const offsetLine = buildParallelRhumbLine(
        start,
        abBearing,
        distance / 1000,
        extentKm
      );

      const intersections = turf.lineIntersect(offsetLine, field);
      if (intersections.features.length < 2) {
        return;
      }

      // Clip to the field: first and last crossing along the offset line.
      const lineStart = turf.point(offsetLine.geometry.coordinates[0]);
      const sortedIntersections = intersections.features
        .slice()
        .sort(
          (a, b) =>
            turf.distance(lineStart, a, { units: "meters" }) -
            turf.distance(lineStart, b, { units: "meters" })
        );
      const entry = sortedIntersections[0];
      const exit = lastItem(sortedIntersections);
      const lineLength = turf.distance(entry, exit, { units: "meters" });

      parallelLines.push(
        turf.lineString(
          [entry.geometry.coordinates, exit.geometry.coordinates],
          {
            id: distance === 0 ? `ab-line-${index}` : `parallel-line-${index}`,
            distance,
            index,
            length: lineLength,
          }
        )
      );
    } catch (error) {
      console.warn(
        `❌ Error creating strip line at distance ${distance}m:`,
        error
      );
    }
  });

  // console.log(`🏁 Line generation complete: ${parallelLines.length} lines created out of ${offsetDistances.length} requested`);
  // console.log("🔚 =================== LINE GENERATION ENDED ===================\n");

  return parallelLines;
};

/**
 * Calculate strip areas between parallel lines within the polygon
 * @param parallelLines - Array of parallel line features
 * @param polygonGeometry - The field polygon
 * @returns Object containing lines and strip data with detailed measurements
 */
export const calculateStripAreas = (
  parallelLines: Array<Feature<LineString>>,
  polygonGeometry: Polygon | MultiPolygon
): StripLineResult => {
  const field = turf.feature(polygonGeometry) as Feature<
    Polygon | MultiPolygon
  >;
  // Sort the strip lines left → right by signed perpendicular offset so that
  // band k sits between line[k-1] and line[k]. N lines ⇒ N+1 bands ⇒ N+1 strips.
  const sortedLines = [...parallelLines].sort(
    (a, b) => (a.properties?.distance ?? 0) - (b.properties?.distance ?? 0)
  );

  const extentKm = computeBandExtentKm(field);
  const strips = buildStripsAcrossField(field, sortedLines, extentKm);
  renumberStripsByPosition(strips, sortedLines);
  warnOnStripAreaMismatch(field, strips);

  return { lines: parallelLines, strips };
};

/**
 * Extent (km) used to stretch the band boundary lines well past the field so
 * each band fully spans it (including every parcel of a MultiPolygon). Falls
 * back to a safe default when the field bbox can't be measured.
 */
function computeBandExtentKm(field: Feature<Polygon | MultiPolygon>): number {
  try {
    const [minX, minY, maxX, maxY] = turf.bbox(field);
    const diagKm = turf.distance([minX, minY], [maxX, maxY], {
      units: "kilometers",
    });
    if (Number.isFinite(diagKm) && diagKm > 0) return diagKm * 2 + 1;
  } catch {
    // keep default extent
  }
  return 50;
}

/**
 * One strip per band between consecutive sorted lines (N lines ⇒ N+1 bands).
 * Empty / zero-area bands (e.g. an outer band that lies beyond the field) are
 * skipped.
 */
function buildStripsAcrossField(
  field: Feature<Polygon | MultiPolygon>,
  sortedLines: Array<Feature<LineString>>,
  extentKm: number
): StripLineResult["strips"] {
  const strips: StripLineResult["strips"] = [];
  const lineCount = sortedLines.length;
  for (let k = 0; k <= lineCount; k++) {
    const leftLine = k > 0 ? sortedLines[k - 1] : null;
    const rightLine = k < lineCount ? sortedLines[k] : null;
    if (!leftLine && !rightLine) continue; // no strip lines at all

    const stripGeometry = buildBandStrip(field, leftLine, rightLine, extentKm);
    if (!stripGeometry || turf.area(stripGeometry) <= 0) continue;

    strips.push(
      createStripInfo(stripGeometry, strips.length, leftLine, rightLine)
    );
  }
  return strips;
}

/**
 * Renumber strips (in place) by on-screen position so labels read consistently
 * no matter which way the AB line points: left → right when the strips are
 * spread more horizontally, otherwise top → bottom (northern strip first). The
 * map labels, strip cells and rate table all read `strip.index` from this array,
 * so renumbering here keeps them in sync. No-op for 0 or 1 strip.
 */
function renumberStripsByPosition(
  strips: StripLineResult["strips"],
  sortedLines: Array<Feature<LineString>>
): void {
  if (strips.length <= 1) return;

  const withCentroid = strips.map((strip) => ({
    strip,
    centroid: turf.centroid(strip.geometry).geometry.coordinates as [
      number,
      number
    ],
  }));

  // Order strips ACROSS the field, i.e. along the axis perpendicular to the AB
  // line. Sorting by raw X (or Y) is only correct when the AB line is roughly
  // vertical (or horizontal); for a diagonal AB line a strip's centroid X is
  // NOT monotonic with its position, so the labels scrambled (e.g. 1,3,5,2,4,6)
  // as the line was moved. Projecting each centroid onto the AB-perpendicular
  // axis is monotonic with the strip's offset at any angle, so the sequence
  // stays 1,2,3,… however the AB line points.
  const abCoords = sortedLines[0]?.geometry?.coordinates;
  const abBearing =
    abCoords && abCoords.length >= 2
      ? turf.bearing(turf.point(abCoords[0]), turf.point(lastItem(abCoords)))
      : 0;
  const perpBearing = abBearing + 90;
  const ref = turf.point(withCentroid[0].centroid);
  const perpProjection = (coordinate: [number, number]): number => {
    const point = turf.point(coordinate);
    const distance = turf.distance(ref, point, { units: "meters" });
    const bearing = turf.bearing(ref, point);
    return distance * Math.cos(((bearing - perpBearing) * Math.PI) / 180);
  };
  withCentroid.sort(
    (a, b) => perpProjection(a.centroid) - perpProjection(b.centroid)
  );

  // Orient the numbering to read left → right, or top → bottom (northern strip
  // first) when the strips are stacked more vertically than horizontally.
  const first = withCentroid[0].centroid;
  const last = lastItem(withCentroid).centroid;
  const horizontal =
    Math.abs(last[0] - first[0]) >= Math.abs(last[1] - first[1]);
  const wrongWay = horizontal ? first[0] > last[0] : first[1] < last[1];
  if (wrongWay) withCentroid.reverse();

  strips.length = 0;
  withCentroid.forEach(({ strip }, position) => {
    strip.index = position;
    strips.push(strip);
  });
}

/**
 * Guardrail: the bands partition the field, so Σ(strip areas) must equal the
 * field area. A >1% mismatch means a disjoint piece was dropped somewhere — warn
 * (best-effort only).
 */
function warnOnStripAreaMismatch(
  field: Feature<Polygon | MultiPolygon>,
  strips: StripLineResult["strips"]
): void {
  try {
    const fieldArea = turf.area(field);
    const stripsArea = strips.reduce((sum, strip) => sum + strip.area, 0);
    const diff = Math.abs(fieldArea - stripsArea);
    if (fieldArea > 0 && diff / fieldArea > 0.01) {
      console.warn(
        `⚠️ Strip area mismatch: field=${fieldArea.toFixed(1)}m² ` +
          `strips=${stripsArea.toFixed(1)}m² ` +
          `(Δ ${((100 * diff) / fieldArea).toFixed(
            2
          )}%) — a piece may be missing.`
      );
    }
  } catch {
    // best-effort guardrail only
  }
}

const STRIP_BAND_EXTEND_FACTOR = 5;

/**
 * Build one strip: the field area inside the band bounded by two parallel strip
 * lines (either may be null for the outermost bands). The band is intersected
 * with every parcel of the field separately and ALL resulting pieces are kept, so
 * a strip can be a Polygon or a MultiPolygon (disjoint across a MultiPolygon's
 * gaps, a concave notch or a hole). This is the fix for the long-standing bug
 * where disjoint strip pieces were silently dropped ("largest only").
 */
function buildBandStrip(
  field: Feature<Polygon | MultiPolygon>,
  leftLine: Feature<LineString> | null,
  rightLine: Feature<LineString> | null,
  extentKm: number
): Feature<Polygon | MultiPolygon> | null {
  const band = buildBandPolygon(leftLine, rightLine, extentKm);
  if (!band) return null;

  const pieces: Array<Polygon["coordinates"]> = [];
  for (const parcel of getSubPolygons(field.geometry)) {
    let clipped: Feature<Polygon | MultiPolygon> | null = null;
    try {
      clipped = turf.intersect(parcel, band) as Feature<
        Polygon | MultiPolygon
      > | null;
    } catch (error) {
      console.warn("❌ Error intersecting strip band with parcel:", error);
      continue;
    }
    if (!clipped) continue;
    if (clipped.geometry.type === "Polygon") {
      pieces.push(clipped.geometry.coordinates);
    } else {
      clipped.geometry.coordinates.forEach((polyCoords) =>
        pieces.push(polyCoords)
      );
    }
  }

  if (pieces.length === 0) return null;
  if (pieces.length === 1) return turf.polygon(pieces[0]);
  return turf.multiPolygon(pieces);
}

/**
 * The slab (rectangle) between two parallel boundary lines. For an outer band one
 * boundary is null, so a far parallel line is synthesised beyond the field on that
 * side. `leftLine` is the lower-offset boundary, `rightLine` the higher-offset one.
 */
function buildBandPolygon(
  leftLine: Feature<LineString> | null,
  rightLine: Feature<LineString> | null,
  extentKm: number
): Feature<Polygon> | null {
  try {
    const refLine = leftLine ?? rightLine;
    if (!refLine) return null;

    const extend = (line: Feature<LineString>): Feature<LineString> => {
      const c = line.geometry.coordinates;
      return extendLine(c[0], lastItem(c), STRIP_BAND_EXTEND_FACTOR);
    };

    const refCoords = refLine.geometry.coordinates;
    // Bearing + 90° is the "increasing offset" direction — matches how
    // generateParallelLines positions positive-distance lines.
    const bearing = turf.bearing(
      turf.point(refCoords[0]),
      turf.point(lastItem(refCoords))
    );

    // In each outer-band case one boundary is null; refLine is the non-null one.
    const lowLine = leftLine
      ? extend(leftLine)
      : turf.transformTranslate(extend(refLine), extentKm, bearing - 90);
    const highLine = rightLine
      ? extend(rightLine)
      : turf.transformTranslate(extend(refLine), extentKm, bearing + 90);

    const low = lowLine.geometry.coordinates;
    const high = highLine.geometry.coordinates;
    const lowStart = low[0];
    const lowEnd = lastItem(low);
    const highStart = high[0];
    const highEnd = lastItem(high);

    // Both boundary lines share the AB orientation, so start↔start / end↔end pair
    // into a simple (non-self-intersecting) trapezoid covering the slab.
    return turf.polygon([[lowStart, lowEnd, highEnd, highStart, lowStart]]);
  } catch (error) {
    console.warn("❌ Error building strip band polygon:", error);
    return null;
  }
}

/** Longest edge of a strip polygon's outer ring, used as an ultimate fallback length. */
function computeLongestEdgeDistance(
  stripPolygon: Feature<Polygon | MultiPolygon>
): number {
  const coords =
    stripPolygon.geometry.type === "Polygon"
      ? stripPolygon.geometry.coordinates[0]
      : stripPolygon.geometry.coordinates[0][0];
  let maxDistance = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const distance = turf.distance(coords[i], coords[i + 1], {
      units: "meters",
    });
    maxDistance = Math.max(maxDistance, distance);
  }
  return maxDistance;
}

/**
 * Strip length/width from the oriented bounding box of the strip polygon,
 * relative to a reference boundary line for orientation.
 */
function computeOrientedStripDimensions(
  stripPolygon: Feature<Polygon | MultiPolygon>,
  referenceLine: Feature<LineString>
): { stripLength: number; stripWidth: number } {
  const orientedBbox = createOrientedBoundingBox(stripPolygon, referenceLine);
  const bboxCoords = orientedBbox.geometry.coordinates[0];

  // Calculate the dimensions of the oriented bounding box
  // The bounding box is a rectangle, so we need to find the two different edge lengths
  const distances: number[] = [];
  for (let i = 0; i < bboxCoords.length - 1; i++) {
    const distance = turf.distance(bboxCoords[i], bboxCoords[i + 1], {
      units: "meters",
    });
    distances.push(distance);
  }

  // Remove duplicates and sort to get the two unique dimensions
  const roundedDistances = distances.map((d) => Math.round(d * 100) / 100);
  const uniqueDistances: number[] = [];
  for (const dist of roundedDistances) {
    if (!uniqueDistances.includes(dist)) {
      uniqueDistances.push(dist);
    }
  }
  uniqueDistances.sort((a, b) => b - a); // Sort descending

  if (uniqueDistances.length >= 2) {
    // Length is the longer dimension (along the strip line direction);
    // width is the shorter dimension (perpendicular to strip lines).
    return { stripLength: uniqueDistances[0], stripWidth: uniqueDistances[1] };
  }
  // Square case - both dimensions are the same
  return { stripLength: uniqueDistances[0], stripWidth: uniqueDistances[0] };
}

/**
 * Strip length/width, preferring the oriented bounding box (relative to
 * whichever boundary line is available), and falling back to the longest
 * edge of the strip polygon when there is no boundary line to orient by.
 */
function computeStripDimensions(
  stripPolygon: Feature<Polygon | MultiPolygon>,
  index: number,
  leftBoundary: Feature<LineString> | null,
  rightBoundary: Feature<LineString> | null,
  area: number
): { stripLength: number; stripWidth: number } {
  try {
    // buildStripsAcrossField skips any band whose left AND right boundary are
    // both null, so computeStripDimensions is never invoked with neither
    // present — the type assertion reflects that caller invariant.
    const referenceLine = (leftBoundary ??
      rightBoundary) as Feature<LineString>;
    return computeOrientedStripDimensions(stripPolygon, referenceLine);
  } catch (error) {
    console.warn(
      `Warning: Could not calculate oriented dimensions for strip ${index}:`,
      error
    );
    const stripLength = computeLongestEdgeDistance(stripPolygon);
    const stripWidth = stripLength > 0 ? area / stripLength : 0;
    return { stripLength, stripWidth };
  }
}

/**
 * Create strip information object from polygon
 */
function createStripInfo(
  stripPolygon: Feature<Polygon | MultiPolygon>,
  index: number,
  leftBoundary: Feature<LineString> | null,
  rightBoundary: Feature<LineString> | null
): {
  index: number;
  area: number;
  stripLength: number;
  stripWidth: number;
  geometry: Feature<Polygon | MultiPolygon>;
  boundingBox: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  };
  leftBoundary: Feature<LineString> | null;
  rightBoundary: Feature<LineString> | null;
} {
  const area = turf.area(stripPolygon);

  const bbox = turf.bbox(stripPolygon);
  const boundingBox = {
    minX: bbox[0],
    minY: bbox[1],
    maxX: bbox[2],
    maxY: bbox[3],
  };

  const { stripLength, stripWidth } = computeStripDimensions(
    stripPolygon,
    index,
    leftBoundary,
    rightBoundary,
    area
  );

  return {
    index,
    area,
    stripLength,
    stripWidth,
    geometry: stripPolygon,
    boundingBox,
    leftBoundary,
    rightBoundary,
  };
}

// Helper function to remove handlers with layer bindings
function removeLayerHandlers(
  map: mapboxgl.Map,
  sourceId: string,
  handlers: Record<string, any>,
  configs: Array<{ key: string; event: string; layer: string }>
): void {
  configs.forEach(({ key, event, layer }) => {
    if (handlers[key]) {
      map.off(event as keyof mapboxgl.MapLayerEventType, layer, handlers[key]);
    }
  });
}

// Helper function to remove handlers with event-only bindings (no layer)
function removeGlobalHandlers(
  map: mapboxgl.Map,
  sourceId: string,
  handlers: Record<string, any>
): void {
  const handlerMap: Array<{ key: string; event: string; layers?: string[] }> = [
    { key: "generalClick", event: "click" },
    {
      key: "dividerMouseDown",
      event: "mousedown",
      layers: [`${sourceId}-controls`, `${sourceId}-controls-divider`],
    },
    {
      key: "dividerTouchStart",
      event: "touchstart",
      layers: [`${sourceId}-controls`, `${sourceId}-controls-divider`],
    },
    { key: "dividerMouseMove", event: "mousemove" },
    { key: "dividerTouchMove", event: "touchmove" },
    { key: "dividerMouseUp", event: "mouseup" },
    { key: "dividerTouchEnd", event: "touchend" },
  ];

  handlerMap.forEach(({ key, event, layers }) => {
    if (handlers[key]) {
      if (layers) {
        layers.forEach((layer) => {
          map.off(
            event as keyof mapboxgl.MapLayerEventType,
            layer,
            handlers[key]
          );
        });
      } else {
        map.off(event as any, handlers[key]);
      }
    }
  });
}

// Utility to clean up all event handlers for a given sourceId
function cleanupStripLineHandlers(map: mapboxgl.Map, sourceId: string): void {
  const existingHandlers = (map as any)._stripLineHandlers || {};
  if (!existingHandlers[sourceId]) return;

  try {
    const handlers = existingHandlers[sourceId];

    // Remove configured layer handlers
    const handlerConfigs = [
      {
        key: "clickLeft",
        event: "click",
        layer: `${sourceId}-controls-left-arrow`,
      },
      {
        key: "clickRight",
        event: "click",
        layer: `${sourceId}-controls-right-arrow`,
      },
      {
        key: "mouseenterLeft",
        event: "mouseenter",
        layer: `${sourceId}-controls-left-arrow`,
      },
      {
        key: "mouseleaveLeft",
        event: "mouseleave",
        layer: `${sourceId}-controls-left-arrow`,
      },
      {
        key: "mouseenterRight",
        event: "mouseenter",
        layer: `${sourceId}-controls-right-arrow`,
      },
      {
        key: "mouseleaveRight",
        event: "mouseleave",
        layer: `${sourceId}-controls-right-arrow`,
      },
      {
        key: "clickCircle",
        event: "click",
        layer: `${sourceId}-controls`,
      },
      {
        key: "mouseenterCircle",
        event: "mouseenter",
        layer: `${sourceId}-controls`,
      },
      {
        key: "mouseleaveCircle",
        event: "mouseleave",
        layer: `${sourceId}-controls`,
      },
    ];
    removeLayerHandlers(map, sourceId, handlers, handlerConfigs);

    // Remove global handlers
    removeGlobalHandlers(map, sourceId, handlers);
  } catch (error) {
    console.warn("Error removing event handlers:", error);
  }
  Reflect.deleteProperty(existingHandlers, sourceId);
}

/** Remove the layers, then sources, previously added for `sourceId` (both lists
 * are safe to call even when nothing exists yet — each entry is checked). */
function removeStaleLayersAndSources(
  map: mapboxgl.Map,
  layerIds: string[],
  sourceIds: string[]
): void {
  layerIds.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  sourceIds.forEach((srcId) => {
    if (map.getSource(srcId)) {
      map.removeSource(srcId);
    }
  });
}

/** Oriented bounding-box feature for one strip's hover outline, aligned with
 * whichever boundary line is available (falling back to any strip line, then
 * to an axis-aligned bbox). */
function buildStripBoundingBoxFeature(
  strip: StripLineResult["strips"][number],
  lines: Array<Feature<LineString>>
): Feature<Polygon> {
  // Try to find a boundary line for orientation
  let stripLine: Feature<LineString> | null = null;
  if (strip.leftBoundary) {
    stripLine = strip.leftBoundary;
  } else if (strip.rightBoundary) {
    stripLine = strip.rightBoundary;
  } else if (lines.length > 0) {
    // Fallback to any available line
    stripLine = lines[0];
  }

  const bboxPolygon = stripLine
    ? createOrientedBoundingBox(strip.geometry, stripLine)
    : turf.bboxPolygon(turf.bbox(strip.geometry));

  return {
    ...bboxPolygon,
    properties: {
      stripIndex: strip.index,
    },
  };
}

/** One strip-number label point (1-based) per disjoint piece of a strip, so a
 * strip split across parcels/a hole still shows its number on every piece. */
function buildStripNumberLabelFeatures(
  strips: StripLineResult["strips"]
): Array<Feature<Point>> {
  return strips.flatMap((strip) =>
    getSubPolygons(strip.geometry.geometry).map((piece) => {
      // Centre the label per polygon exactly like a plot cell
      // (turf.centerOfMass). For a concave/clipped strip piece the centre
      // of mass can fall outside the ring, so fall back to a guaranteed
      // interior point so the number never lands in a gap.
      let labelPoint: Feature<Point>;
      try {
        const center = turf.centerOfMass(piece) as Feature<Point>;
        labelPoint = turf.booleanPointInPolygon(center, piece)
          ? center
          : (turf.pointOnFeature(piece) as Feature<Point>);
      } catch {
        labelPoint = turf.centroid(piece) as Feature<Point>;
      }
      return {
        type: "Feature" as const,
        geometry: labelPoint.geometry,
        properties: {
          stripIndex: strip.index,
          // 1-based, left → right.
          label: `${strip.index + 1}`,
        },
      };
    })
  );
}

/** Midpoint + bearing/perpendicular-bearing for each strip line's control
 * point, used both for the on-map divider handle and the click-arrow layers. */
function buildStripLineControlPoints(
  lines: Array<Feature<LineString>>
): Array<Feature<Point>> {
  return lines.map((line, index) => {
    const lineCoords = line.geometry.coordinates;
    const startPoint = lineCoords[0];
    const endPoint = lastItem(lineCoords);

    // Calculate midpoint
    const midpoint = [
      (startPoint[0] + endPoint[0]) / 2,
      (startPoint[1] + endPoint[1]) / 2,
    ];

    // Calculate the bearing of the strip line for perpendicular rotation
    const bearing = turf.bearing(turf.point(startPoint), turf.point(endPoint));

    // Perpendicular angle to the strip line (90 degrees offset)
    const perpendicularBearing = bearing + 90;
    return {
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: midpoint,
      },
      properties: {
        stripLineIndex: index,
        distance: line.properties?.distance || 0,
        bearing,
        perpendicularBearing,
      },
    };
  });
}

/** Draggable-divider features (one per strip line), cloned from the control
 * points so their coordinates/properties can be mutated independently while
 * dragging. */
function buildDividerFeatures(
  controlPoints: Array<Feature<Point>>
): Array<Feature<Point>> {
  return controlPoints.map((controlPoint) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [...controlPoint.geometry.coordinates],
    },
    properties: {
      stripLineIndex: controlPoint.properties?.stripLineIndex,
      distance: controlPoint.properties?.distance,
      // Line bearing so the drag arrow can be rotated perpendicular to it.
      bearing: controlPoint.properties?.bearing,
      isActive: 0,
      // Set true while dragging over an occupied grid line → red circle.
      invalid: false,
    },
  }));
}

/**
 * Draw strip lines and areas on the map
 * @param map - Mapbox map instance
 * @param stripResult - Result from calculateStripAreas
 * @param sourceId - Base source ID for the layers
 * @param options - Styling options
 * @param onStripLineAdjust - Callback function for strip line adjustments
 */
/** Adds the dashed strip-boundary lines layer (no-op if there are no lines). */
function addStripLinesLayer(
  map: mapboxgl.Map,
  stripResult: StripLineResult,
  sourceId: string,
  lineColor: string,
  lineWidth: number
): void {
  if (stripResult.lines.length === 0) {
    return;
  }
  const linesCollection: FeatureCollection<LineString> = {
    type: "FeatureCollection",
    features: stripResult.lines.map((line, index) => ({
      type: "Feature" as const,
      // Render the FULL line so it stays continuous (dashed) across the gaps of
      // a MultiPolygon field — otherwise the drag handle, which sits at the
      // line midpoint, can float in a gap with no line under it.
      geometry: line.geometry,
      properties: {
        ...line.properties,
        stripLineIndex: index,
      },
    })),
  };

  map.addSource(`${sourceId}-lines-source`, {
    type: "geojson",
    data: linesCollection,
  });

  map.addLayer({
    id: `${sourceId}-lines`,
    type: "line",
    source: `${sourceId}-lines-source`,
    paint: {
      "line-color": lineColor,
      "line-width": lineWidth,
      "line-dasharray": [5, 5], // Dashed lines for visibility
    },
  });

  // No labels on the strip lines themselves (matches production — the line's
  // length/offset is not shown). The strip NUMBER labels are added separately
  // below. The `${sourceId}-line-labels` layer is still cleaned up above, so
  // any lingering label from an older render is removed.
}

/** Adds the coloured strip-area fill/hover/label layers (no-op if there are no strips). */
function addStripAreasLayer(
  map: mapboxgl.Map,
  stripResult: StripLineResult,
  sourceId: string,
  stripColors: string[],
  showLabels: boolean
): void {
  if (stripResult.strips.length === 0) {
    return;
  }
  const stripsCollection: FeatureCollection<Polygon | MultiPolygon> = {
    type: "FeatureCollection",
    features: stripResult.strips.map((strip, index) => ({
      ...strip.geometry,
      properties: {
        ...strip.geometry.properties,
        // Remove alpha channel from colors for Mapbox compatibility
        color: stripColors[index % stripColors.length].replace(
          /[0-9a-fA-F]{2}$/,
          ""
        ),
        stripIndex: strip.index,
        area: strip.area,
        stripLength: strip.stripLength,
        stripWidth: strip.stripWidth,
      },
    })),
  };

  map.addSource(`${sourceId}-strips-source`, {
    type: "geojson",
    data: stripsCollection,
  });

  // Transparent fill over each strip's FULL geometry. A strip that spans
  // several parcels is a MultiPolygon, so this single per-strip feature covers
  // EVERY disjoint piece — the hover handlers below (bound to this layer) then
  // fire when the pointer is over any piece, not just one. Opacity 0 keeps the
  // rate-coloured cell fills (rendered separately) visible; a fully
  // transparent fill still receives mouse events in mapbox.
  map.addLayer({
    id: `${sourceId}-strips`,
    type: "fill",
    source: `${sourceId}-strips-source`,
    paint: { "fill-color": "#000000", "fill-opacity": 0 },
  });

  // Create oriented bounding box geometries for hover effect (aligned with strip lines)
  const boundingBoxCollection: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: stripResult.strips.map((strip) =>
      buildStripBoundingBoxFeature(strip, stripResult.lines)
    ),
  };

  // Add bounding box source
  map.addSource(`${sourceId}-strips-bbox-source`, {
    type: "geojson",
    data: boundingBoxCollection,
  });

  // Add bounding box layer for hover effect (initially hidden)
  map.addLayer({
    id: `${sourceId}-strips-bbox`,
    type: "line",
    source: `${sourceId}-strips-bbox-source`,
    paint: {
      "line-color": "rgba(0, 0, 0, 0)",
      "line-width": 3,
      "line-dasharray": [4, 4],
    },
    filter: ["==", ["get", "stripIndex"], -1], // Initially hide all bounding boxes
  });

  // Add hover event handlers for strip bounding boxes
  const handleStripMouseEnter = (e: any): void => {
    if (e.features.length > 0) {
      const feature = e.features[0];
      const stripIndex = feature.properties?.stripIndex;

      if (stripIndex !== undefined) {
        // Show bounding box for the hovered strip
        map.setFilter(`${sourceId}-strips-bbox`, [
          "==",
          ["get", "stripIndex"],
          stripIndex,
        ]);
        map.getCanvas().style.cursor = "pointer";
      }
    }
  };

  const handleStripMouseLeave = (): void => {
    // Hide all bounding boxes
    map.setFilter(`${sourceId}-strips-bbox`, ["==", ["get", "stripIndex"], -1]);
    map.getCanvas().style.cursor = "";
  };

  // Store strip hover handlers for cleanup
  if (!(map as any)._stripHoverHandlers) {
    (map as any)._stripHoverHandlers = {};
  }
  (map as any)._stripHoverHandlers[sourceId] = {
    mouseenter: handleStripMouseEnter,
    mouseleave: handleStripMouseLeave,
  };

  // Add hover event listeners for strips
  map.on("mouseenter", `${sourceId}-strips`, handleStripMouseEnter);
  map.on("mouseleave", `${sourceId}-strips`, handleStripMouseLeave);

  // Add a strip-number label (1..N, numbered left → right) on EVERY disjoint
  // piece of each strip. A strip that spans several parcels (MultiPolygon)
  // therefore shows the same number on each of its parts. pointOnFeature keeps
  // the label inside the piece even for concave shapes or holes.
  if (showLabels) {
    const stripNumberLabels = buildStripNumberLabelFeatures(stripResult.strips);

    const stripNumberLabelsCollection = {
      type: "FeatureCollection" as const,
      features: stripNumberLabels,
    };

    map.addSource(`${sourceId}-strip-area-labels-source`, {
      type: "geojson",
      data: stripNumberLabelsCollection,
    });

    // Match the regular plot CELL label style (addLabelToMap "plot"): 12px,
    // centred, thin halo — not the bigger/bolder group label.
    map.addLayer({
      id: `${sourceId}-strip-area-labels`,
      type: "symbol",
      source: `${sourceId}-strip-area-labels-source`,
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-anchor": "center",
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1.5,
      },
    });
  }
}

/**
 * Resolves the divider's current offset: prefers the divider feature's own
 * tracked distance, falling back to the strip line's distance property. Do
 * not read current distance back through snap-guide indices because those
 * indices no longer align once midpoint guides are included.
 */
function resolveCurrentLineDistance(
  parsedFeatureDistance: number,
  stripLineIndex: number,
  stripResult: StripLineResult
): number {
  if (Number.isFinite(parsedFeatureDistance)) {
    return parsedFeatureDistance;
  }
  if (Number.isFinite(stripLineIndex) && stripResult.lines[stripLineIndex]) {
    return Number(stripResult.lines[stripLineIndex].properties?.distance ?? 0);
  }
  return 0;
}

/**
 * Adds the draggable divider control buttons/icons and their drag/drop event
 * handlers (no-op if there is no adjust callback or no strip lines).
 */
function addStripDividerControls(
  map: mapboxgl.Map,
  stripResult: StripLineResult,
  sourceId: string,
  onStripLineAdjust:
    | ((data: {
        direction: "left" | "right";
        indexOfStripLine: number;
        action?: "step" | "snap";
        snappedDistance?: number;
      }) => void)
    | undefined,
  snapGuideLines: Array<Feature<LineString>>
): void {
  if (!onStripLineAdjust || stripResult.lines.length === 0) {
    return;
  }
  // Create center points for each strip line
  const controlPoints = buildStripLineControlPoints(stripResult.lines);

  const dividerFeatures: Array<Feature<Point>> =
    buildDividerFeatures(controlPoints);

  const dividerCollection: FeatureCollection<Point> = {
    type: "FeatureCollection",
    features: dividerFeatures,
  };

  // ONE source drives both the circle background and the arrow so they move
  // together while dragging.
  map.addSource(`${sourceId}-controls-divider-source`, {
    type: "geojson",
    data: dividerCollection,
  });

  // Highlight for the grid/grey line the divider is about to snap onto (blue
  // when droppable, red when that line is already taken by another strip).
  map.addSource(`${sourceId}-drop-highlight-source`, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });
  map.addLayer({
    id: `${sourceId}-drop-highlight`,
    type: "line",
    source: `${sourceId}-drop-highlight-source`,
    paint: {
      "line-color": [
        "case",
        ["boolean", ["get", "invalid"], false],
        DROP_HIGHLIGHT_INVALID_COLOR,
        DROP_HIGHLIGHT_VALID_COLOR,
      ],
      "line-width": 4,
    },
  });

  // Circle background — same source as the arrow, red when the target is taken.
  map.addLayer({
    id: `${sourceId}-controls`,
    type: "circle",
    source: `${sourceId}-controls-divider-source`,
    paint: {
      "circle-radius": 15,
      "circle-color": [
        "case",
        ["boolean", ["get", "invalid"], false],
        CONTROL_CIRCLE_INVALID_COLOR,
        CONTROL_CIRCLE_COLOR,
      ],
      "circle-stroke-color": "rgba(20, 20, 20, 0.9)",
      "circle-stroke-width": 1.5,
      "circle-opacity": 0.8,
    },
  });

  // Build the drag handle icon on a canvas and register it SYNCHRONOUSLY, so
  // the handle shows immediately (no async Image.onload). Two DISJOINT arrows
  // ( <-   -> ) with a centre gap, so the strip line shows through the gap. The
  // per-feature `icon-rotate = bearing` turns it perpendicular to the line.
  const dividerIconId = `${sourceId}-divider-icon`;
  if (!map.hasImage(dividerIconId)) {
    const iconSize = 40;
    const canvas = document.createElement("canvas");
    canvas.width = iconSize;
    canvas.height = iconSize;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const cy = iconSize / 2;
      const cx = iconSize / 2;
      const head = 6;
      const leftTip = 6;
      const rightTip = iconSize - 6;
      const gapHalf = 5; // half-width of the centre gap the strip line shows through
      ctx.strokeStyle = "#ffffff";
      ctx.fillStyle = "#ffffff";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      // Left arrow ( <- ): shaft from the gap edge out to the head.
      ctx.beginPath();
      ctx.moveTo(cx - gapHalf, cy);
      ctx.lineTo(leftTip + head, cy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(leftTip, cy);
      ctx.lineTo(leftTip + head, cy - head);
      ctx.lineTo(leftTip + head, cy + head);
      ctx.closePath();
      ctx.fill();
      // Right arrow ( -> ): shaft from the gap edge out to the head.
      ctx.beginPath();
      ctx.moveTo(cx + gapHalf, cy);
      ctx.lineTo(rightTip - head, cy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rightTip, cy);
      ctx.lineTo(rightTip - head, cy - head);
      ctx.lineTo(rightTip - head, cy + head);
      ctx.closePath();
      ctx.fill();
      map.addImage(dividerIconId, ctx.getImageData(0, 0, iconSize, iconSize));
    }
  }

  map.addLayer({
    id: `${sourceId}-controls-divider`,
    type: "symbol",
    source: `${sourceId}-controls-divider-source`,
    layout: {
      "icon-image": dividerIconId,
      "icon-size": 0.8,
      "icon-rotate": ["get", "bearing"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-anchor": "center",
    },
    paint: {
      "icon-opacity": 1,
    },
  });

  const enableStepControls = stepControlsConfig.enabled;

  // Remove any existing event listeners for this sourceId first
  cleanupStripLineHandlers(map, sourceId);

  // Factory function to create click handlers for arrow icons
  const createArrowClickHandler = (
    direction: "left" | "right"
  ): ((e: any) => void) => {
    return (e: any) => {
      // Add click effect for entire circle
      map.setPaintProperty(`${sourceId}-controls`, "circle-radius", 13);
      map.setPaintProperty(
        `${sourceId}-controls`,
        "circle-color",
        "rgba(20, 20, 20, 1.0)"
      );

      setTimeout(() => {
        map.setPaintProperty(`${sourceId}-controls`, "circle-radius", 15);
        map.setPaintProperty(
          `${sourceId}-controls`,
          "circle-color",
          "rgba(40, 40, 40, 0.8)"
        );
        map.getCanvas().style.cursor = "default";
      }, 150);

      const layerName = `${sourceId}-controls-${direction}-arrow`;
      const features = map.queryRenderedFeatures(e.point, {
        layers: [layerName],
      });

      if (features.length > 0) {
        const stripLineIndex = features[0].properties?.stripLineIndex;
        if (stripLineIndex !== undefined) {
          try {
            onStripLineAdjust({
              direction,
              indexOfStripLine: stripLineIndex,
            });
          } catch (callbackError) {
            console.error(
              "🎯 Error in onStripLineAdjust callback:",
              callbackError
            );
          }
        }
      }
    };
  };

  const handleLeftClick = createArrowClickHandler("left");
  const handleRightClick = createArrowClickHandler("right");

  // Factory function to create hover state handlers for arrow icons
  const createArrowHoverHandler = (
    hoverState: "enter" | "leave"
  ): (() => void) => {
    const isEnter = hoverState === "enter";
    return () => {
      map.getCanvas().style.cursor = isEnter ? "pointer" : "default";
      map.setPaintProperty(
        `${sourceId}-controls`,
        "circle-radius",
        isEnter ? 17 : 15
      );
      map.setPaintProperty(
        `${sourceId}-controls`,
        "circle-opacity",
        isEnter ? 1.0 : 0.8
      );
      map.setPaintProperty(
        `${sourceId}-controls`,
        "circle-color",
        isEnter ? "rgba(60, 60, 60, 0.95)" : "rgba(40, 40, 40, 0.8)"
      );
    };
  };

  const handleLeftMouseEnter = createArrowHoverHandler("enter");
  const handleLeftMouseLeave = createArrowHoverHandler("leave");
  const handleRightMouseEnter = createArrowHoverHandler("enter");
  const handleRightMouseLeave = createArrowHoverHandler("leave");

  const setDividerData = (): void => {
    const source = map.getSource(
      `${sourceId}-controls-divider-source`
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(dividerCollection);
    }
  };

  const snapLines =
    snapGuideLines.length > 0 ? snapGuideLines : stripResult.lines;

  const lineMidpointByIndex: Record<number, [number, number]> = {};
  snapLines.forEach((line, index) => {
    const lineCoords = line.geometry.coordinates;
    const start = lineCoords[0];
    const end = lastItem(lineCoords);
    lineMidpointByIndex[index] = [
      (start[0] + end[0]) / 2,
      (start[1] + end[1]) / 2,
    ];
  });

  const lineDistanceMetersByIndex: Record<number, number> = {};
  snapLines.forEach((line, index) => {
    lineDistanceMetersByIndex[index] = Number(line.properties?.distance ?? 0);
  });

  const sortedLineIndices = Object.keys(lineDistanceMetersByIndex)
    .map(Number)
    .sort(
      (a, b) => lineDistanceMetersByIndex[a] - lineDistanceMetersByIndex[b]
    );

  // ── Collision / drop-target feedback ──────────────────────────────────────
  // The snap line (grid/grey) nearest to a cursor position.
  const getNearestSnapIndex = (lngLat: {
    lng: number;
    lat: number;
  }): number | null => {
    if (!snapLines || snapLines.length === 0) return null;
    const point = turf.point([lngLat.lng, lngLat.lat]);
    let bestIndex: number | null = null;
    let bestDistance = Infinity;
    snapLines.forEach((line, index) => {
      const distance = turf.pointToLineDistance(point, line, {
        units: "meters",
      });
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  };

  // True when another strip line already sits at `offset` (they would overlap).
  const isOffsetOccupied = (
    offset: number,
    excludeStripLineIndex: number
  ): boolean =>
    stripResult.lines.some((line, index) => {
      if (index === excludeStripLineIndex) return false;
      const lineDistance = Number(line.properties?.distance ?? 0);
      return Math.abs(lineDistance - offset) < OCCUPIED_TOLERANCE_M;
    });

  const dropHighlightSourceId = `${sourceId}-drop-highlight-source`;
  const updateDropHighlight = (
    line: Feature<LineString> | null,
    invalid: boolean
  ): void => {
    const source = map.getSource(
      dropHighlightSourceId
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: line
        ? [
            {
              type: "Feature",
              geometry: line.geometry,
              properties: { invalid },
            },
          ]
        : [],
    });
  };
  const clearDropHighlight = (): void => {
    updateDropHighlight(null, false);
  };

  // Flag the dragged handle valid/invalid so its circle turns red.
  const setDraggedInvalid = (invalid: boolean): void => {
    if (dragState.draggedFeatureIndex < 0) return;
    const feature = dividerFeatures[dragState.draggedFeatureIndex];
    if (feature?.properties) {
      feature.properties.invalid = invalid;
    }
    setDividerData();
  };

  const isPointInsideField = (lngLat: {
    lng: number;
    lat: number;
  }): boolean => {
    const point = turf.point([lngLat.lng, lngLat.lat]);
    return stripResult.strips.some((strip) =>
      turf.booleanPointInPolygon(point, strip.geometry, {
        ignoreBoundary: false,
      })
    );
  };

  const getNearestLineByDropPoint = (lngLat: {
    lng: number;
    lat: number;
  }): { lineIndex: number; distanceMeters: number } | null => {
    if (!snapLines || snapLines.length === 0) {
      return null;
    }

    const point = turf.point([lngLat.lng, lngLat.lat]);
    let nearestIndex = -1;
    let nearestDistance = Number.POSITIVE_INFINITY;

    snapLines.forEach((line, index) => {
      const distanceMeters = turf.pointToLineDistance(point, line, {
        units: "meters",
      });
      if (Number.isFinite(distanceMeters) && distanceMeters < nearestDistance) {
        nearestDistance = distanceMeters;
        nearestIndex = index;
      }
    });

    if (nearestIndex < 0) {
      return null;
    }

    return { lineIndex: nearestIndex, distanceMeters: nearestDistance };
  };

  const getNearestLineByDropEvent = (
    _e: any,
    lngLat: { lng: number; lat: number }
  ): { lineIndex: number; distanceMeters: number } | null => {
    return getNearestLineByDropPoint(lngLat);
  };

  const getNearestOutermostLineIndex = (lngLat: {
    lng: number;
    lat: number;
  }): number => {
    if (sortedLineIndices.length === 1) {
      return sortedLineIndices[0];
    }

    const point = turf.point([lngLat.lng, lngLat.lat]);
    const first = sortedLineIndices[0];
    const last = lastItem(sortedLineIndices);
    const firstDistance = turf.pointToLineDistance(point, snapLines[first], {
      units: "meters",
    });
    const lastDistance = turf.pointToLineDistance(point, snapLines[last], {
      units: "meters",
    });
    return firstDistance <= lastDistance ? first : last;
  };

  const dragState: {
    isDragging: boolean;
    draggedFeatureIndex: number;
    startCoordinates: [number, number] | null;
  } = {
    isDragging: false,
    draggedFeatureIndex: -1,
    startCoordinates: null,
  };

  const resetDraggedDividerVisualState = (): void => {
    const feature = dividerFeatures[dragState.draggedFeatureIndex];
    if (feature?.properties) {
      feature.properties.isActive = 0;
    }
    setDividerData();
  };

  const updateDraggedDividerToLngLat = (lngLat: mapboxgl.LngLat): void => {
    const feature = dividerFeatures[dragState.draggedFeatureIndex];
    feature.geometry.coordinates = [lngLat.lng, lngLat.lat];
    setDividerData();
  };

  const resolveEventLngLat = (
    e: any,
    fallback: [number, number]
  ): { lng: number; lat: number } => {
    const directLng = Number(e?.lngLat?.lng);
    const directLat = Number(e?.lngLat?.lat);
    if (Number.isFinite(directLng) && Number.isFinite(directLat)) {
      return { lng: directLng, lat: directLat };
    }

    const pointX = Number(e?.point?.x);
    const pointY = Number(e?.point?.y);
    if (Number.isFinite(pointX) && Number.isFinite(pointY)) {
      const projected = map.unproject([pointX, pointY]);
      const projectedLng = Number(projected?.lng);
      const projectedLat = Number(projected?.lat);
      if (Number.isFinite(projectedLng) && Number.isFinite(projectedLat)) {
        return { lng: projectedLng, lat: projectedLat };
      }
    }

    return {
      lng: Number.isFinite(fallback[0]) ? fallback[0] : 0,
      lat: Number.isFinite(fallback[1]) ? fallback[1] : 0,
    };
  };

  const beginDividerDrag = (e: any): void => {
    if (typeof e?.preventDefault === "function") {
      e.preventDefault();
    }

    let feature = e?.features?.[0];

    if (!feature && e?.point) {
      const queriedFeatures = map.queryRenderedFeatures(e.point, {
        layers: [`${sourceId}-controls-divider`, `${sourceId}-controls`],
      });
      feature = queriedFeatures[0];
    }

    if (!feature) {
      return;
    }

    const stripLineIndex = Number(feature.properties?.stripLineIndex);
    const dividerFeatureIndex = dividerFeatures.findIndex(
      (dividerFeature) =>
        Number(dividerFeature.properties?.stripLineIndex) === stripLineIndex
    );

    if (dividerFeatureIndex < 0) {
      return;
    }

    dragState.isDragging = true;
    dragState.draggedFeatureIndex = dividerFeatureIndex;
    dragState.startCoordinates = [
      dividerFeatures[dividerFeatureIndex].geometry.coordinates[0],
      dividerFeatures[dividerFeatureIndex].geometry.coordinates[1],
    ];

    dividerFeatures[dividerFeatureIndex].properties = {
      ...dividerFeatures[dividerFeatureIndex].properties,
      isActive: 1,
    };

    map.getCanvas().style.cursor = "grabbing";
    map.dragPan.disable();
    setDividerData();
  };

  // Live drop feedback while dragging: highlight the nearest grid/grey line
  // the handle would snap to, in red when another strip already occupies
  // it. Split out of handleDividerMove purely to keep that function's
  // cognitive complexity down — behaviour is unchanged.
  const updateLiveDropFeedback = (
    moveLngLat: { lng: number; lat: number },
    draggedStripLineIndex: number
  ): void => {
    const snapIndex = getNearestSnapIndex(moveLngLat);
    if (snapIndex != null) {
      const invalid = isOffsetOccupied(
        lineDistanceMetersByIndex[snapIndex],
        draggedStripLineIndex
      );
      updateDropHighlight(snapLines[snapIndex], invalid);
      setDraggedInvalid(invalid);
    } else {
      clearDropHighlight();
      setDraggedInvalid(false);
    }
  };

  const handleDividerMove = (e: any): void => {
    if (!dragState.isDragging) {
      return;
    }
    if (typeof e?.preventDefault === "function") {
      e.preventDefault();
    }
    const draggedFeature = dividerFeatures[dragState.draggedFeatureIndex];
    const fallbackCoords = draggedFeature
      ? ([
          draggedFeature.geometry.coordinates[0],
          draggedFeature.geometry.coordinates[1],
        ] as [number, number])
      : dragState.startCoordinates ?? [0, 0];
    const moveLngLat = resolveEventLngLat(e, fallbackCoords);
    updateDraggedDividerToLngLat(moveLngLat as mapboxgl.LngLat);

    const draggedStripLineIndex = Number(
      draggedFeature?.properties?.stripLineIndex ?? -1
    );
    updateLiveDropFeedback(moveLngLat, draggedStripLineIndex);
  };

  const endDividerDrag = (e: any): void => {
    if (!dragState.isDragging || dragState.draggedFeatureIndex < 0) {
      return;
    }

    const draggedFeature = dividerFeatures[dragState.draggedFeatureIndex];
    const droppedLngLat = resolveEventLngLat(e, [
      draggedFeature.geometry.coordinates[0],
      draggedFeature.geometry.coordinates[1],
    ]);

    const originalCoordinates =
      dragState.startCoordinates ??
      ([
        draggedFeature.geometry.coordinates[0],
        draggedFeature.geometry.coordinates[1],
      ] as [number, number]);

    const insideField = isPointInsideField(droppedLngLat);
    const stripLineIndex = Number(draggedFeature.properties?.stripLineIndex);
    const nearestLine = getNearestLineByDropEvent(e, droppedLngLat);

    const parsedFeatureDistance = Number(
      draggedFeature.properties?.distance ?? 0
    );

    // The divider feature distance tracks the actual strip offset. Do not read
    // current distance back through snap-guide indices because those indices no
    // longer align once midpoint guides are included.
    const currentLineDistance = resolveCurrentLineDistance(
      parsedFeatureDistance,
      stripLineIndex,
      stripResult
    );

    const outermostLineIndex = insideField
      ? null
      : getNearestOutermostLineIndex(droppedLngLat);

    const { targetDistance, targetCoords } = resolveDividerDropTarget({
      insideField,
      droppedLngLat,
      originalCoordinates,
      stripLineIndex,
      currentLineDistance,
      nearestLineIndex: nearestLine?.lineIndex ?? null,
      outermostLineIndex,
      lineDistanceMetersByIndex,
      lineMidpointByIndex,
      fallbackLineIndex:
        Number.isFinite(stripLineIndex) && stripLineIndex >= 0
          ? stripLineIndex
          : sortedLineIndices[0] ?? 0,
    });

    const targetOccupied =
      insideField && isOffsetOccupied(targetDistance, stripLineIndex);

    if (targetOccupied) {
      // Another strip already sits on this grid line — reject the drop: snap
      // the handle back to where the drag started and warn the user.
      draggedFeature.geometry.coordinates = [
        originalCoordinates[0],
        originalCoordinates[1],
      ];
      toast.error(formatTranslation(i18n.t("trials.invalidStripPosition")));
    } else {
      draggedFeature.geometry.coordinates = [targetCoords[0], targetCoords[1]];

      const callbackIndex = stripLineIndex;

      if (Number.isFinite(callbackIndex)) {
        onStripLineAdjust({
          direction: "right",
          indexOfStripLine: callbackIndex,
          action: "snap",
          snappedDistance: targetDistance,
        });
      }
    }

    if (draggedFeature.properties) {
      draggedFeature.properties.invalid = false;
    }
    clearDropHighlight();
    setDividerData();

    resetDraggedDividerVisualState();
    dragState.isDragging = false;
    dragState.draggedFeatureIndex = -1;
    dragState.startCoordinates = null;
    map.getCanvas().style.cursor = "default";
    map.dragPan.enable();
  };

  // Store handlers for cleanup
  if (!(map as any)._stripLineHandlers) {
    (map as any)._stripLineHandlers = {};
  }
  (map as any)._stripLineHandlers[sourceId] = {
    clickLeft: handleLeftClick,
    clickRight: handleRightClick,
    mouseenterLeft: handleLeftMouseEnter,
    mouseleaveLeft: handleLeftMouseLeave,
    mouseenterRight: handleRightMouseEnter,
    mouseleaveRight: handleRightMouseLeave,
    dividerMouseDown: beginDividerDrag,
    dividerTouchStart: beginDividerDrag,
    dividerMouseMove: handleDividerMove,
    dividerTouchMove: handleDividerMove,
    dividerMouseUp: endDividerDrag,
    dividerTouchEnd: endDividerDrag,
  };

  // console.log("🎯 Adding event listeners...");

  // Add click event listeners for arrow icons only (not the circle background)
  if (enableStepControls) {
    map.on("click", `${sourceId}-controls-left-arrow`, handleLeftClick);
    map.on("click", `${sourceId}-controls-right-arrow`, handleRightClick);
  }

  // console.log("🎯 Added click listeners for arrow icons only");

  // Add hover event listeners for arrow icons only (not the circle background)
  if (enableStepControls) {
    map.on(
      "mouseenter",
      `${sourceId}-controls-left-arrow`,
      handleLeftMouseEnter
    );
    map.on(
      "mouseleave",
      `${sourceId}-controls-left-arrow`,
      handleLeftMouseLeave
    );
    map.on(
      "mouseenter",
      `${sourceId}-controls-right-arrow`,
      handleRightMouseEnter
    );
    map.on(
      "mouseleave",
      `${sourceId}-controls-right-arrow`,
      handleRightMouseLeave
    );
  }

  map.on("mousedown", `${sourceId}-controls`, beginDividerDrag);
  map.on("mousedown", `${sourceId}-controls-divider`, beginDividerDrag);
  map.on("touchstart", `${sourceId}-controls`, beginDividerDrag);
  map.on("touchstart", `${sourceId}-controls-divider`, beginDividerDrag);
  map.on("mousemove", handleDividerMove);
  map.on("touchmove", handleDividerMove);
  map.on("mouseup", endDividerDrag);
  map.on("touchend", endDividerDrag);

  // console.log("🎯 Added hover listeners for arrow icons only");

  // console.log("🎯 === CONTROL BUTTONS SETUP COMPLETE ===");
}

export const drawStripLinesToMap = (
  map: mapboxgl.Map,
  stripResult: StripLineResult,
  sourceId: string,
  snapGuideLines: Array<Feature<LineString>> = [],
  options: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  } = {},
  // Field used to clip the rendered strip lines per-parcel so they don't bridge
  // the gaps of a MultiPolygon field. Optional — falls back to the full line.
  fieldGeometry?: Polygon | MultiPolygon
): void => {
  const {
    lineColor = "#000000",
    lineWidth = 2,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    stripOpacity = 0.3,
    stripColors = ["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff"],
    showLabels = true,
    onStripLineAdjust,
  } = options;

  // Remove existing layers and sources with event handlers cleanup
  const layerIds = [
    `${sourceId}-lines`,
    `${sourceId}-line-labels`,
    `${sourceId}-strips`,
    `${sourceId}-strips-bbox`,
    `${sourceId}-strip-area-labels`,
    `${sourceId}-labels`,
    `${sourceId}-drop-highlight`,
    `${sourceId}-controls`,
    `${sourceId}-controls-divider`,
    `${sourceId}-controls-left-arrow`,
    `${sourceId}-controls-right-arrow`,
  ];

  // Clean up existing event handlers first
  cleanupStripLineHandlers(map, sourceId);

  // Clean up existing strip hover handlers
  const existingStripHoverHandlers = (map as any)._stripHoverHandlers || {};
  if (existingStripHoverHandlers[sourceId]) {
    try {
      map.off(
        "mouseenter",
        `${sourceId}-strips`,
        existingStripHoverHandlers[sourceId].mouseenter
      );
      map.off(
        "mouseleave",
        `${sourceId}-strips`,
        existingStripHoverHandlers[sourceId].mouseleave
      );
    } catch (error) {
      console.warn("Error removing strip hover handlers:", error);
    }
    // Use Reflect.deleteProperty instead of delete operator
    Reflect.deleteProperty(existingStripHoverHandlers, sourceId);
  }

  const sourceIds = [
    `${sourceId}-lines-source`,
    `${sourceId}-strips-source`,
    `${sourceId}-strips-bbox-source`,
    `${sourceId}-strip-area-labels-source`,
    `${sourceId}-controls-source`,
    `${sourceId}-controls-divider-source`,
    `${sourceId}-drop-highlight-source`,
  ];

  removeStaleLayersAndSources(map, layerIds, sourceIds);

  // Add lines source and layer
  addStripLinesLayer(map, stripResult, sourceId, lineColor, lineWidth);
  addStripAreasLayer(map, stripResult, sourceId, stripColors, showLabels);
  addStripDividerControls(
    map,
    stripResult,
    sourceId,
    onStripLineAdjust,
    snapGuideLines
  );
};

/**
 * Main function to create and draw strip lines
 * @param map - Mapbox map instance
 * @param abLine - The baseline
 * @param offsetDistances - Array of offset distances in meters
 * @param polygonGeometry - The field polygon
 * @param sourceId - Base source ID for the layers
 * @param options - Styling options
 */
export const createAndDrawStripLines = (
  map: mapboxgl.Map,
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: Polygon | MultiPolygon,
  sourceId: string,
  options?: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  }
): StripLineResult => {
  // Generate parallel lines
  const parallelLines = generateParallelLines(
    abLine,
    offsetDistances,
    polygonGeometry
  );
  const snapGuideLines = getSnapGuideLines(abLine, polygonGeometry);

  // Calculate strip areas
  const stripResult = calculateStripAreas(parallelLines, polygonGeometry);

  // Draw to map (pass the field so strip lines are clipped per-parcel)
  drawStripLinesToMap(
    map,
    stripResult,
    sourceId,
    snapGuideLines,
    options,
    polygonGeometry
  );

  return stripResult;
};

/**
 * Create an oriented bounding box for a polygon aligned with a given line
 * @param polygon - The polygon to create bounding box for
 * @param referenceLine - The line to align the bounding box with
 * @returns Oriented bounding box as a polygon feature
 */
function createOrientedBoundingBox(
  polygon: Feature<Polygon | MultiPolygon>,
  referenceLine: Feature<LineString>
): Feature<Polygon> {
  try {
    // Get the bearing of the reference line using turf
    const lineCoords = referenceLine.geometry.coordinates;
    const startPoint = lineCoords[0];
    const endPoint = lastItem(lineCoords);
    const bearing = turf.bearing(turf.point(startPoint), turf.point(endPoint));

    // Get the center of the polygon for coordinate transformation
    const center = turf.centroid(polygon);
    const centerCoords = center.geometry.coordinates;

    // Gather every vertex of the polygon (all rings, all parcels for a
    // MultiPolygon) so the oriented box covers the whole strip geometry.
    const polygonCoords: number[][] =
      polygon.geometry.type === "Polygon"
        ? polygon.geometry.coordinates.flat()
        : polygon.geometry.coordinates.flat(2);

    // Transform coordinates to a local coordinate system with the line bearing as reference
    const transformedCoords: Array<{ x: number; y: number }> = [];

    for (const coord of polygonCoords) {
      if (coord.length >= 2) {
        // Calculate distance and bearing from center to this point
        const point = turf.point(coord);
        const distance = turf.distance(center, point, { units: "meters" });
        const pointBearing = turf.bearing(center, point);

        // Calculate relative bearing to the reference line
        const relativeBearing = pointBearing - bearing;
        const relativeRadians = (relativeBearing * Math.PI) / 180;

        // Project onto line-aligned coordinate system
        // x = along the line direction, y = perpendicular to the line
        const x = distance * Math.cos(relativeRadians);
        const y = distance * Math.sin(relativeRadians);

        transformedCoords.push({ x, y });
      }
    }

    if (transformedCoords.length === 0) {
      throw new Error("No valid coordinates found");
    }

    // Find the bounding box in the transformed coordinate system
    const minX = Math.min(...transformedCoords.map((c) => c.x));
    const maxX = Math.max(...transformedCoords.map((c) => c.x));
    const minY = Math.min(...transformedCoords.map((c) => c.y));
    const maxY = Math.max(...transformedCoords.map((c) => c.y));

    // Create the corners of the bounding box in local coordinates
    const localCorners = [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
      { x: minX, y: minY }, // Close the polygon
    ];

    // Transform back to geographic coordinates
    const geoCorners: number[][] = [];

    for (const corner of localCorners) {
      // Calculate distance and bearing from center for this corner
      const distance = Math.hypot(corner.x, corner.y);
      const localBearing = (Math.atan2(corner.y, corner.x) * 180) / Math.PI;

      // Convert back to geographic bearing
      const geoBearing = bearing + localBearing;

      if (distance > 0) {
        // Use turf.destination to get the geographic coordinate
        const destination = turf.destination(
          center,
          distance / 1000, // Convert meters to kilometers
          geoBearing,
          { units: "kilometers" }
        );
        geoCorners.push(destination.geometry.coordinates);
      } else {
        // If distance is 0, use the center point
        geoCorners.push([...centerCoords]);
      }
    }

    return turf.polygon([geoCorners]);
  } catch (error) {
    console.warn("❌ Error creating oriented bounding box:", error);
    // Fallback to axis-aligned bounding box
    const bbox = turf.bbox(polygon);
    return turf.bboxPolygon(bbox);
  }
}
